import { useEffect, useRef } from "react";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getPaddleEnvironment } from "@/lib/paddle";

/**
 * Subscribes to billing_events for the current user and surfaces them
 * as toasts. On mount, also catches up any unread events from the last
 * 24h that haven't been shown yet.
 */
export function BillingNotifications() {
  const { userId, mode, isAuthenticated } = useAuth();
  const seenIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isAuthenticated || mode !== "real" || !userId) return;

    const env = getPaddleEnvironment();

    const showToast = (row: any) => {
      if (seenIds.current.has(row.id)) return;
      seenIds.current.add(row.id);
      const amount = row.amount_cents
        ? new Intl.NumberFormat(undefined, { style: "currency", currency: row.currency ?? "USD" })
            .format(row.amount_cents / 100)
        : "";
      if (row.event_type === "invoice_paid") {
        toast.success(`Invoice paid${amount ? ` · ${amount}` : ""}`, {
          description: row.invoice_number ? `Receipt ${row.invoice_number} sent by Paddle.` : "Receipt sent by Paddle.",
          icon: <CheckCircle2 className="h-4 w-4" />,
          duration: 6000,
        });
      } else if (row.event_type === "payment_failed") {
        toast.error(`Payment failed${amount ? ` · ${amount}` : ""}`, {
          description: "Update your card in Settings to keep dashboard access.",
          icon: <AlertTriangle className="h-4 w-4" />,
          duration: 10000,
        });
      }
      // Mark read
      supabase.from("billing_events").update({ read_at: new Date().toISOString() }).eq("id", row.id);
    };

    // 1) Catch up unread events from last 24h
    (async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from("billing_events")
        .select("*")
        .eq("user_id", userId)
        .eq("environment", env)
        .is("read_at", null)
        .gte("created_at", since)
        .order("created_at", { ascending: true });
      (data ?? []).forEach(showToast);
    })();

    // 2) Realtime stream of new events
    const ch = supabase
      .channel(`billing_events_${userId}`)
      .on("postgres_changes", {
        event: "INSERT", schema: "public", table: "billing_events",
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        const row: any = payload.new;
        if (row?.environment !== env) return;
        showToast(row);
      })
      .subscribe();

    return () => { supabase.removeChannel(ch); };
  }, [userId, mode, isAuthenticated]);

  return null;
}